import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import { useAuth } from "../context/AuthContext";
import { useCategory } from "../context/CategoryContext";
import { showError, showSuccess } from "../utils/toast"; // adjust path


const CustomerList = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // local filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("All");
  const [selectedType, setSelectedType] = useState("All");
  const [selectedMonth, setSelectedMonth] = useState("All");
  const [selectedYear, setSelectedYear] = useState("All");

  const [customers, setCustomers] = useState([]);

  // category comes from context (read-only here)
  const { selectedCategory } = useCategory();

  const toggleSidebar = () => setSidebarOpen((s) => !s);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const agent = user || JSON.parse(localStorage.getItem("user"));
        if (!agent?.id) {
          console.warn("❌ Agent ID not found.");
          return;
        }

        const res = await fetch(
          `${import.meta.env.VITE_API_URL}/api/Insurance-by-Category?insuranceType=${selectedCategory}&agentId=${agent.id}`
        );
        const data = await res.json();

        const policies = Array.isArray(data) ? data : [];

        const formatted = policies.map((p, index) => ({
          id: p._id || index,
          name: p.customerName || "Unknown",
          phone: p.customerPhone,
          email: p.customerEmail,
          company: p.company || "Unknown",
          category: p.insuranceType || "Unknown",
          type: p.policyType || "Unknown",
          premium: Number(p.policyDetails?.premium || 0),
          dueDate: p.policyDetails?.endDate || "",
          policyId: p.policyNumber || "",
          sumAssured: p.policyDetails?.sumInsured || "",
        }));

        setCustomers(formatted);
      } catch (err) {
        console.error("❌ Error fetching customers:", err);
      }
    };

    fetchCustomers();
  }, [user, selectedCategory]);

  const companyOptions = [
    "All",
    ...new Set(customers.map((c) => c.company).filter(Boolean)),
  ];


  const filtered = customers.filter((c) => {
    const nameMatch =
      (c.name?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
      (c.phone || "").includes(searchTerm);

    const companyMatch =
      selectedCompany === "All" || c.company === selectedCompany;
    const categoryMatch =
      selectedCategory === "All" || c.category === selectedCategory;
    const typeMatch = selectedType === "All" || c.type === selectedType;

    const dueMonth = c.dueDate?.slice(5, 7);
    const dueYear = c.dueDate?.slice(0, 4);

    const monthMatch = selectedMonth === "All" || dueMonth === selectedMonth;
    const yearMatch = selectedYear === "All" || dueYear === selectedYear;

    return (
      nameMatch &&
      companyMatch &&
      categoryMatch &&
      typeMatch &&
      monthMatch &&
      yearMatch
    );
  });

  const handleExport = () => {
    const csv = [
      [
        "Customer",
        "Phone",
        "Email",
        "Company",
        "Category",
        "Insurance Type",
        "Policy ID",
        "Sum Assured",
        "Premium",
        "Due Date",
      ],
      ...filtered.map((c) => [
        c.name,
        c.phone,
        c.email,
        c.company,
        c.category,
        c.type,
        c.policyId,
        c.sumAssured,
        c.premium,
        c.dueDate,
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "customer_data.csv";
    link.click();
  };

  const handleClear = () => {
    setSearchTerm("");
    setSelectedCompany("All");
    setSelectedType("All");
    setSelectedMonth("All");
    setSelectedYear("All");
    // NOTE: selectedCategory is owned by context, so we do not change it here.
  };

  const uniqueInsuranceTypes = [...new Set(customers.filter((c) => c.category === selectedCategory).map((c) => c.type))];
  return (
    <div className="dashboard-layout d-flex customers-page">
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
      <div className="main-content flex-grow-1">
        <div className="container py-4">
          {/* Header */}
          <div className="cp-header d-flex align-items-center flex-wrap gap-2 mb-4 pt-0 border-0">
            <div className="d-flex align-items-center gap-2">
              <span className="cp-title-icon">
                <i className="bi bi-people" />
              </span>
              <div>
                <h2 className="m-0 cp-title">Customers</h2>
                <p className="m-0 cp-sub">
                  Manage all your customers and their insurance policies.
                </p>
              </div>
            </div>
          </div>

          {/* Filters (compact, responsive, no horizontal scroll) */}
          <div className="card border-0 shadow-sm rounded-4 cp-filters mb-4">
            <div className="card-body">
              <div className="row g-2 gy-2 align-items-center cp-compact">
                {/* Search */}
                <div className="col-12 col-md-4 col-xl-3">
                  <div className="input-with-icon">
                    <i className="bi bi-search" />
                    <input
                      type="text"
                      className="form-control cp-control"
                      placeholder="Search by Customer Name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>

                {/* Month */}
                <div className="col-6 col-md-3 col-xl-3">
                  <div className="select-with-caret">
                    <select
                      className="form-select cp-control"
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                    >
                      <option value="All">All Months</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option key={i} value={String(i + 1).padStart(2, "0")}>
                          {new Date(0, i).toLocaleString("default", { month: "long" })}
                        </option>
                      ))}
                    </select>
                    <i className="bi bi-caret-down-fill" />
                  </div>
                </div>

                {/* Year */}
                <div className="col-6 col-md-3 col-xl-3">
                  <div className="select-with-caret">
                    <select
                      className="form-select cp-control"
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                    >
                      <option value="All">All Years</option>
                      {Array.from(new Set(customers.map((c) => c.dueDate?.slice(0, 4))))
                        .filter(Boolean)
                        .map((year) => (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        ))}
                    </select>
                    <i className="bi bi-caret-down-fill" />
                  </div>
                </div>

                {/* Clear */}
                <div className="col-6 col-md-3 col-xl-3">
                  <button
                    className="btn btn-pill teal-outline cp-control-btn"
                    onClick={handleClear}
                  >
                    Clear
                  </button>
                </div>

                {/* spacer for small screens */}
                <div className="col-12 d-md-none" />

                {/* Company */}
                <div className="col-6 col-md-3 col-xl-3">
                  <div className="select-with-caret">
                    <select
                      className="form-select cp-control"
                      value={selectedCompany}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                    >
                      <option value="All">All Companies</option>
                      {companyOptions.map((comp) => (
                        <option key={comp} value={comp}>
                          {comp}
                        </option>
                      ))}
                    </select>
                    <i className="bi bi-caret-down-fill" />
                  </div>
                </div>

                {/* Category (read-only, NO arrow) */}
                <div className="col-6 col-md-3 col-xl-3">
                  <div className="select-with-caret no-caret">
                    <select
                      className="form-select cp-control"
                      value={selectedCategory}
                      disabled
                      onChange={() => { }}
                    >
                      <option value={selectedCategory}>
                        {selectedCategory === "All" ? "Life & General" : selectedCategory}
                      </option>
                    </select>
                    {/* no custom caret here */}
                  </div>
                </div>

                {/* Type */}
                <div className="col-6 col-md-3 col-xl-3">
                  <div className="select-with-caret">
                    <select
                      className="form-select cp-control"
                      value={selectedType}
                      onChange={(e) => setSelectedType(e.target.value)}
                      disabled={selectedCategory === "All"}
                    >
                      <option value="All">All Types</option>

                      {selectedCategory !== "All" &&
                        uniqueInsuranceTypes.map((type) => (
                          <option
                            key={type}
                            value={type}
                          >
                            {type}
                          </option>
                        ))
                      }
                    </select>

                    <i className="bi bi-caret-down-fill" />
                  </div>
                </div>



                {/* Export */}
                <div className="col-12 col-md-6 col-xl-3 order-md-2 order-xl-0">
                  <button
                    className="btn btn-pill teal cp-control-btn"
                    onClick={handleExport}
                  >
                    <i className="bi bi-download me-2" />
                    Export Data
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-0">
              <div className="table-responsive">
                {/* Added modifier class `customers-table--striped` */}
                <table className="table  table-bordered table-hover customers-table customers-table--striped align-middle mb-0">
                  <thead>
                    <tr>
                      <th>S.N</th>
                      <th>Policy ID</th>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Company</th>
                      <th>Insurance Type</th>
                      <th>Premium</th>
                      <th>Due</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4 text-muted">
                          No matching data found.
                        </td>
                      </tr>
                    ) : (
                      filtered.map((row, idx) => (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>{row.policyId}</td>
                          <td>{row.name}</td>
                          <td>{row.phone}</td>
                          <td>{row.company}</td>
                          <td>{row.type}</td>
                          <td>₹{row.premium.toLocaleString()}</td>
                          <td>{row.dueDate}</td>
                          <td className="text-center">
                            <button
                              className="icon-btn"
                              onClick={() =>
                                navigate(`/agent/customer/${row.id.split("-")[0]}`)
                              }
                              title="View"
                            >
                              <i className="bi bi-eye" />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* /Table */}
        </div>
      </div>
    </div>
  );
};

export default CustomerList;
